// Pure derivation: chat progress events → the stage rows of the step timeline.
//
// The backend (api_server/routes/chat.py) streams `progress` events with a
// raw `step` key; several raw steps belong to one user-facing stage (e.g.
// `reranking` + `score_cutoff` → "Reranking"). Heartbeats ("Still planning…
// (4s)") re-use their step key and only prove the stage is still running.
//
// The same function serves two data shapes, so it must not rely on either:
//   - live ActiveStreams state: every event appended, a run of heartbeats
//     collapsed into its latest one;
//   - server-stored turns: every non-heartbeat event appended, server `ts`.
// Older live states overwrote each step's entry (and its `ts`), so callers
// can still pass the first time they saw each raw step (`marks.firstSeen`).
//
// Timing tags (servers since 2026-10-30): events say where their time goes —
// `kind` llm (with `model`) / remote (with `service`) / local — and the
// answer reports its first token and its end (tokens, tok/s). Turns without
// them render as before (no bars, "N steps · X s" summary).

import type { PlanningEvent, RoutingEvent, SourceDoc, StepDetail, StepKind } from '../../types';
import { countSourceDocs } from '../../lib/sourceDocs';
import { MODE_LABELS, strategyTarget, toolArgSummary } from './strategies';

export type StageKey =
  | 'understand'
  | 'plan'
  | 'prefilter'
  | 'search'
  | 'refine'
  | 'rerank'
  | 'context'
  | 'generate';

export const STAGE_ORDER: StageKey[] = [
  'understand', 'plan', 'prefilter', 'search', 'refine', 'rerank', 'context', 'generate',
];

const STEP_STAGE: Record<string, StageKey> = {
  condensing: 'understand',
  routing: 'understand',
  planning: 'plan',
  prefilter: 'prefilter',
  retrieving: 'search',
  strategy_done: 'search',
  per_doc_cap: 'search',
  iterative: 'refine',
  iterative_check: 'refine',
  web_search_fallback: 'refine',
  reranking: 'rerank',
  score_cutoff: 'rerank',
  neighbor_expand: 'context',
  full_doc_expand: 'context',
  context_budget: 'context',
  context: 'context',
  generating: 'generate',
};

/** User-facing stage of a raw progress step (unknown steps → undefined). */
export const stageOf = (step: string): StageKey | undefined => STEP_STAGE[step];

/** planner = planned pipeline, agent = tool loop, vector = plain hybrid search,
 *  chat = no retrieval, pending = routing not known yet. */
export type TurnKind = 'planner' | 'agent' | 'vector' | 'chat' | 'pending';
export type RowStatus = 'pending' | 'active' | 'done' | 'skipped' | 'error';

export interface TimelineSubRow {
  id: string;
  name: string;        // strategy / tool key ('' for the agent's "next step" row)
  target?: string;     // what it ran on: entity, indicator, query …
  status: RowStatus;
  count?: number;
  unit?: string;
  title?: string;      // full result / error text for the tooltip
  llmS?: number;       // LLM seconds inside this strategy (HyDE)
  model?: string;
}

/** The answer's split: waiting for the first token, then streaming. */
export interface AnswerSplit {
  firstTokenTs?: number;   // server ts of the first token
  ttftS?: number;          // seconds from the stage start to the first token
  tokens?: number;
  tokensEstimated?: boolean;
  tokS?: number;
  done: boolean;
}

export interface TimelineStage {
  key: StageKey;
  label: string;
  sublabel?: string;
  status: RowStatus;
  startTs?: number;    // unix seconds
  durationS?: number;  // set for finished stages whose end is known
  count?: number;
  unit?: string;
  messages: string[];  // distinct non-heartbeat messages, oldest first
  subRows: TimelineSubRow[];
  /** Where the stage's time goes (timed turns only). */
  kind?: StepKind;
  model?: string;
  service?: string;    // 'gpufkie', or 'fallback' for a remote service on its CPU copy
  /** LLM seconds of a finished stage (whole stage for llm-kind stages,
   *  the rounds' LLM time for agent research). */
  llmS?: number;
  answer?: AnswerSplit;
}

export interface Timeline {
  kind: TurnKind;
  live: boolean;
  stages: TimelineStage[];
  active: TimelineStage | null;
  startTs?: number;
  answerStartTs?: number;
  /** Seconds from the first step until the answer started being written. */
  retrievalS?: number;
  sourceDocs: number;
  summarizedDocs: number;
  /** Events carry timing tags: bars, chips and the LLM/retrieval summary. */
  timed: boolean;
  /** Finished stages: LLM seconds and everything else. */
  llmS: number;
  workS: number;
  /** Live answer length in characters (token estimate while streaming). */
  answerChars?: number;
}

export interface TimelineMarks {
  startedAt?: number;                  // client time the turn was sent
  firstSeen?: Record<string, number>;  // first ts seen per raw step key
  endedAt?: number;                    // client time the stream finished
}

export interface TimelineInput {
  streaming?: boolean;
  hasContent?: boolean;
  stepDetails?: StepDetail[];
  planningEvent?: PlanningEvent | null;
  routingEvent?: RoutingEvent | null;
  sources?: SourceDoc[];
  marks?: TimelineMarks;
  answerChars?: number;
}

const HEARTBEAT_RE = /\(\d+s\)\s*$/;
export const isHeartbeat = (message: string | undefined): boolean =>
  !!message && HEARTBEAT_RE.test(message);

export const cleanMessage = (m: string | undefined): string =>
  (m ?? '').replace(/(\.{2,}|…)+\s*$/, '').trimEnd();

const STRATEGY_DONE_RE = /^(\S+) returned (\d+) chunks?/;
const AGENT_STEP_RE = /^Agent step \d+:\s*([A-Za-z_]+)(?:\((.*)\))?\s*(?:→\s*(.*))?$/;
const AGENT_START_RE = /^Agent (researching|investigating)/;
const AGENT_FALLBACK_RE = /^Agent unavailable/;

interface StrategyEvent {
  message: string;
  count?: number;
  llmS?: number;
  model?: string;
  kind?: StepKind;
}

interface StageAcc {
  key: StageKey;
  startTs?: number;
  /** ts of the stage's closing event when that event carries a result count
   *  ("… · 40 chunks"); undefined while work may still be going on. */
  endTs?: number;
  messages: string[];
  count?: number;
  countMessage?: string;
  strategies: StrategyEvent[];
  kind?: StepKind;
  model?: string;
  service?: string;
  added?: string[];
  answer?: AnswerSplit;
}

function newAcc(key: StageKey): StageAcc {
  return { key, messages: [], strategies: [] };
}

const minTs = (a: number | undefined, b: number | undefined): number | undefined =>
  a == null ? b : b == null ? a : Math.min(a, b);

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

function turnKind(input: TimelineInput, agentAttempt: boolean, agentFellBack: boolean): TurnKind {
  const mode = input.routingEvent?.mode;
  if (mode === 'llm_only') return 'chat';
  if (input.planningEvent?.source === 'agent') return 'agent';
  if (agentAttempt && !agentFellBack) return 'agent';
  if (mode === 'vector') return 'vector';
  if (input.routingEvent || input.planningEvent) return 'planner';
  return 'pending';
}

const PREDICTED: Record<TurnKind, StageKey[]> = {
  planner: ['plan', 'search', 'rerank', 'generate'],
  agent: ['search', 'generate'],
  vector: ['search', 'rerank', 'generate'],
  chat: ['generate'],
  pending: ['search', 'generate'],
};

export function deriveTimeline(input: TimelineInput): Timeline {
  const streaming = !!input.streaming;
  const hasContent = !!input.hasContent;
  const firstSeen = input.marks?.firstSeen ?? {};
  const accs = new Map<StageKey, StageAcc>();
  const timed = (input.stepDetails ?? []).some((d) => !!d?.kind);
  let lastStage: StageKey | undefined;
  let agentAttempt = false;
  let agentFellBack = false;

  const accFor = (key: StageKey): StageAcc => {
    let a = accs.get(key);
    if (!a) {
      a = newAcc(key);
      accs.set(key, a);
    }
    return a;
  };

  // ── 1. Fold events into per-stage accumulators ──────────────────────
  for (const d of input.stepDetails ?? []) {
    if (!d || typeof d.step !== 'string') continue;
    const stage = stageOf(d.step) ?? lastStage ?? 'search';
    const msg = d.message ?? '';

    if (d.step === 'planning' && AGENT_FALLBACK_RE.test(msg)) {
      // The agent loop gave up; the planned pipeline runs next. Its time
      // counts towards planning, and its tool calls are not this turn's search.
      agentFellBack = true;
      const tried = accs.get('search');
      accs.delete('search');
      const plan = accFor('plan');
      plan.startTs = minTs(plan.startTs, minTs(tried?.startTs, d.ts));
      plan.messages.push(cleanMessage(msg));
      lastStage = 'plan';
      continue;
    }

    const acc = accFor(stage);
    let start = d.ts;
    // Live state overwrites a step's ts with each heartbeat; the caller's
    // first-seen time restores the real start. After an agent fallback the
    // first `retrieving` belonged to the agent, not to this search.
    if (!(agentFellBack && stage === 'search')) start = minTs(start, firstSeen[d.step]);
    acc.startTs = minTs(acc.startTs, start);
    lastStage = stage;

    if (d.step === 'retrieving' && AGENT_START_RE.test(msg)) agentAttempt = true;
    const heartbeat = isHeartbeat(msg);
    const isResult =
      !heartbeat && d.step !== 'strategy_done' && (d.count != null || d.phase === 'done');
    acc.endTs = isResult ? d.ts : undefined;
    if (heartbeat) continue;

    if (d.step === 'strategy_done') {
      acc.strategies.push({ message: msg, count: d.count, llmS: d.llm_s, model: d.model, kind: d.kind });
      continue;
    }
    // An LLM stage stays one (a later local note in it doesn't change where
    // its time went); otherwise the latest tag wins — the reranker's done
    // event says who actually served it.
    if (d.kind && acc.kind !== 'llm') acc.kind = d.kind;
    if (d.model && !acc.model) acc.model = d.model;
    if (d.kind) acc.service = d.service;
    if (d.added) acc.added = d.added;
    if (stage === 'generate' && (d.phase === 'first_token' || d.phase === 'done')) {
      const a: AnswerSplit = (acc.answer ??= { done: false });
      if (d.phase === 'first_token') a.firstTokenTs = d.ts;
      if (d.ttft_s != null) a.ttftS = d.ttft_s;
      if (d.phase === 'done') {
        a.done = true;
        a.tokens = d.tokens;
        a.tokensEstimated = d.tokens_estimated;
        a.tokS = d.tok_s;
        if (a.firstTokenTs == null && d.ts != null && d.duration_s != null && a.ttftS != null) {
          a.firstTokenTs = d.ts - d.duration_s + a.ttftS;
        }
      }
      continue; // the split is shown as its own lines, not as messages
    }
    const clean = cleanMessage(msg);
    if (clean && !acc.messages.includes(clean)) acc.messages.push(clean);
    if (d.count != null && !/^History budget/.test(msg)) {
      acc.count = d.count;
      acc.countMessage = clean;
    }
  }

  const kind = turnKind(input, agentAttempt, agentFellBack);
  const pe = input.planningEvent ?? null;

  // ── 2. Stages that exist without progress events ────────────────────
  if (kind !== 'chat' && !accs.has('understand') && (input.routingEvent || streaming)) {
    accs.set('understand', { ...newAcc('understand'), startTs: input.marks?.startedAt });
  }
  if (pe && pe.source !== 'agent' && kind === 'planner' && !accs.has('plan')) {
    // Heuristic / manual plans are decided without an LLM call or progress.
    accs.set('plan', newAcc('plan'));
  }
  if (streaming && hasContent && !accs.has('generate')) accs.set('generate', newAcc('generate'));
  if (!streaming && hasContent && kind !== 'chat' && !accs.has('generate') && accs.size > 0) {
    accs.set('generate', newAcc('generate'));
  }

  // ── 3. Which stage is running ───────────────────────────────────────
  const idx = (k: StageKey) => STAGE_ORDER.indexOf(k);
  let activeKey: StageKey | null = null;
  if (streaming) {
    if (hasContent) activeKey = 'generate';
    else {
      activeKey = lastStage ?? 'understand';
      // A plan that has arrived means search is up next, even before its
      // first progress event lands.
      if (pe && pe.source !== 'agent' && kind === 'planner' && idx(activeKey) <= idx('plan')) {
        activeKey = 'search';
      }
      if (!accs.has(activeKey)) accs.set(activeKey, newAcc(activeKey));
    }
  }

  // ── 4. Predicted, not-yet-started stages (live only) ────────────────
  const presentKeys = STAGE_ORDER.filter((k) => accs.has(k));
  const furthest = Math.max(...presentKeys.map(idx), activeKey ? idx(activeKey) : -1);
  const predicted = new Set<StageKey>();
  if (streaming && !hasContent) {
    for (const k of PREDICTED[kind]) {
      if (!accs.has(k) && idx(k) > furthest) predicted.add(k);
    }
  }
  const keys = STAGE_ORDER.filter((k) => accs.has(k) || predicted.has(k));

  // ── 5. Build rows ───────────────────────────────────────────────────
  const stages: TimelineStage[] = keys.map((key) => {
    const acc = accs.get(key);
    let status: RowStatus;
    if (!acc) status = 'pending';
    else if (activeKey === key) status = 'active';
    else status = 'done';
    return {
      key,
      label: stageLabel(key, kind),
      status,
      startTs: acc?.startTs,
      count: acc?.count,
      messages: acc?.messages ?? [],
      subRows: [],
      ...(timed && acc
        ? {
            kind: acc.kind ?? 'local',
            model: acc.model,
            service: acc.service,
            answer: key === 'generate' ? acc.answer ?? { done: false } : undefined,
          }
        : {}),
    };
  });

  // Durations. The pipeline is sequential, and some stages (reranking,
  // context expansion) only report once they are done: a stage starts where
  // the previous one reported its result. "Writing answer" keeps its own
  // start — the gap before it is graph/context assembly nobody reports.
  // A stage ends at its result event, else where the next stage begins.
  const started = stages.filter((s) => s.startTs != null);
  for (let i = 1; i < started.length; i++) {
    const prevEnd = accs.get(started[i - 1].key)?.endTs;
    const s = started[i];
    if (s.key !== 'generate' && prevEnd != null && prevEnd <= s.startTs!) s.startTs = prevEnd;
  }
  for (let i = 0; i < started.length; i++) {
    const s = started[i];
    if (s.status !== 'done') continue;
    const end =
      accs.get(s.key)?.endTs ??
      (i + 1 < started.length
        ? started[i + 1].startTs
        : s.key === 'generate' ? input.marks?.endedAt : undefined);
    if (end != null && end >= s.startTs!) s.durationS = end - s.startTs!;
  }

  for (const s of stages) decorate(s, accs.get(s.key), input, kind);

  // LLM vs everything else, over finished stages.
  let llmS = 0;
  let workS = 0;
  if (timed) {
    for (const s of stages) {
      if (s.durationS == null) continue;
      const agentLlm =
        s.key === 'search' && kind === 'agent'
          ? (accs.get('search')?.strategies ?? []).reduce((n, e) => n + (e.llmS ?? 0), 0)
          : 0;
      s.llmS = agentLlm > 0 ? Math.min(agentLlm, s.durationS) : s.kind === 'llm' ? s.durationS : 0;
      llmS += s.llmS;
      workS += s.durationS - s.llmS;
    }
  }

  const startTs = started.length ? Math.min(...started.map((s) => s.startTs!)) : undefined;
  const answerStartTs = stages.find((s) => s.key === 'generate')?.startTs;
  const docs = countSourceDocs(input.sources ?? []);
  return {
    kind,
    live: streaming,
    stages,
    active: stages.find((s) => s.status === 'active') ?? null,
    startTs,
    answerStartTs,
    retrievalS:
      startTs != null && answerStartTs != null && answerStartTs >= startTs
        ? answerStartTs - startTs
        : undefined,
    sourceDocs: docs.full,
    summarizedDocs: docs.summary,
    timed,
    llmS,
    workS,
    answerChars: input.answerChars,
  };
}

function stageLabel(key: StageKey, kind: TurnKind): string {
  switch (key) {
    case 'understand': return 'Understanding query';
    case 'plan': return 'Planning';
    case 'prefilter': return 'Pre-filtering corpus';
    case 'search': return kind === 'agent' ? 'Researching' : 'Searching';
    case 'refine': return 'Refining search';
    case 'rerank': return 'Reranking';
    case 'context': return 'Building context';
    case 'generate': return 'Writing answer';
  }
}

/** Fill sublabel, count unit and sub-rows of one stage. */
function decorate(s: TimelineStage, acc: StageAcc | undefined, input: TimelineInput, kind: TurnKind) {
  const pe = input.planningEvent ?? null;
  switch (s.key) {
    case 'understand': {
      const mode = input.routingEvent?.mode;
      if (mode) s.sublabel = MODE_LABELS[mode] ?? mode;
      break;
    }
    case 'plan': {
      if (pe && pe.source !== 'agent') {
        const by =
          pe.fallback ? 'fallback plan'
          : pe.source === 'heuristic' ? 'heuristic'
          : pe.source === 'manual' ? 'manual'
          : pe.model || 'LLM';
        // A timed LLM plan names its model in the chip instead.
        const chip = s.kind === 'llm' && pe.source === 'llm' && !pe.fallback;
        if (chip) s.model ??= pe.model ?? undefined;
        s.sublabel = [chip ? '' : by, pe.query_type].filter(Boolean).join(' · ');
      }
      break;
    }
    case 'prefilter':
      s.unit = 'docs';
      break;
    case 'search': {
      s.unit = kind === 'agent' ? plural(s.count ?? 0, 'passage') : plural(s.count ?? 0, 'chunk');
      s.subRows = kind === 'agent' ? agentRows(s, acc, pe) : kind === 'planner' ? strategyRows(s, acc, pe) : [];
      if (kind === 'vector') s.sublabel = 'vector + BM25';
      else if (kind === 'agent') {
        const n = s.subRows.filter((r) => r.name).length;
        if (n > 0) s.sublabel = `${n} tool ${plural(n, 'call')}`;
      } else if (s.subRows.length > 0) {
        s.sublabel = `${s.subRows.length} ${plural(s.subRows.length, 'strategy', 'strategies')}`;
      }
      break;
    }
    case 'rerank': {
      s.unit = 'kept';
      const model = acc?.messages
        .map((m) => m.match(/^(.+?) \((?:local|remote|local fallback)\) ·/)?.[1])
        .find(Boolean);
      if (model) s.sublabel = model;
      break;
    }
    case 'refine':
      s.unit = plural(s.count ?? 0, 'chunk');
      break;
    case 'context':
      s.unit = kind === 'agent' ? plural(s.count ?? 0, 'passage') : plural(s.count ?? 0, 'chunk');
      if (acc?.added?.length) s.sublabel = acc.added.join(' · ');
      break;
    case 'generate': {
      const first = acc?.messages[0];
      // A timed answer names its model in the chip.
      if (first && !/^Generating/.test(first) && !(s.kind === 'llm' && s.model === first)) s.sublabel = first;
      s.count = undefined; // generating counts are citation warnings, not a size
      break;
    }
  }
}

/** Planned strategies, each resolved by its `strategy_done` event. */
function strategyRows(s: TimelineStage, acc: StageAcc | undefined, pe: PlanningEvent | null): TimelineSubRow[] {
  const rows: TimelineSubRow[] = (pe?.strategies ?? []).map((p, i) => ({
    id: `${p.name}#${i}`,
    name: p.name,
    target: strategyTarget(p.params) || undefined,
    status: 'pending' as RowStatus,
  }));
  const reports = acc?.strategies ?? [];
  for (const ev of reports) {
    const m = ev.message.match(STRATEGY_DONE_RE);
    const name = m?.[1] ?? ev.message.split(/\s/)[0] ?? '';
    const count = ev.count ?? (m ? Number(m[2]) : undefined);
    const row = rows.find((r) => r.name === name && r.status === 'pending');
    const llm = ev.llmS != null ? { llmS: ev.llmS, model: ev.model } : {};
    if (row) {
      row.status = 'done';
      row.count = count;
      Object.assign(row, llm);
    } else {
      rows.push({ id: `${name}#x${rows.length}`, name, status: 'done', count, ...llm });
    }
  }
  for (const r of rows) {
    r.unit = r.count != null ? plural(r.count, 'chunk') : undefined;
    if (r.status !== 'pending') continue;
    // Strategies dropped after planning (IOC quiet mode) never report back.
    if (s.status === 'active') r.status = 'active';
    else if (s.status === 'done') r.status = reports.length > 0 ? 'skipped' : 'done';
  }
  return rows;
}

/** Agent tool calls: the final trace when present, else the live events. */
function agentRows(s: TimelineStage, acc: StageAcc | undefined, pe: PlanningEvent | null): TimelineSubRow[] {
  let rows: TimelineSubRow[];
  if (pe?.source === 'agent' && (pe.agent_trace?.length || pe.strategies.length)) {
    const trace = pe.agent_trace ?? pe.strategies.map((t, i) => ({
      step: i + 1, tool: t.name, args: t.params ?? {}, docs: undefined, summary: undefined, error: null,
    }));
    rows = trace.map((t, i) => ({
      id: `${t.tool}#${i}`,
      name: t.tool,
      target: toolArgSummary(t.tool, t.args) || undefined,
      status: (t.error ? 'error' : 'done') as RowStatus,
      count: t.docs ?? undefined,
      unit: t.docs != null ? plural(t.docs, 'doc') : undefined,
      title: t.error || t.summary || undefined,
    }));
  } else {
    rows = (acc?.strategies ?? []).map((ev, i) => {
      const m = ev.message.match(AGENT_STEP_RE);
      const result = m?.[3]?.trim();
      return {
        id: `agent#${i}`,
        name: m?.[1] ?? 'tool',
        target: m?.[2]?.trim() || undefined,
        status: 'done' as RowStatus,
        count: ev.count,
        unit: ev.count != null ? plural(ev.count, 'doc') : undefined,
        title: result || undefined,
      };
    });
  }
  if (s.status === 'active') {
    rows.push({ id: 'agent#next', name: '', target: rows.length ? 'Choosing next step' : 'Choosing first tool', status: 'active' });
  }
  return rows;
}

/** The collapsed one-liner: "LLM 107 s · retrieval 11 s · 42 sources" for
 *  timed turns, "6 steps · 8.4 s · 42 sources" for older ones. */
export function timelineSummary(t: Timeline): string {
  const parts: string[] = [];
  const n = t.stages.length;
  if (t.timed && t.llmS + t.workS > 0) {
    if (t.llmS > 0) parts.push(`LLM ${formatSeconds(t.llmS)}`);
    if (t.workS > 0) parts.push(`retrieval ${formatSeconds(t.workS)}`);
  } else {
    if (n > 0) parts.push(`${n} ${plural(n, 'step')}`);
    if (t.retrievalS != null) parts.push(formatSeconds(t.retrievalS));
  }
  if (t.sourceDocs > 0 || t.summarizedDocs > 0) {
    let src = `${t.sourceDocs} ${plural(t.sourceDocs, 'source')}`;
    if (t.summarizedDocs > 0) src += ` + ${t.summarizedDocs} summarized`;
    parts.push(src);
  }
  return parts.join(' · ');
}

export function formatSeconds(s: number): string {
  if (!(s >= 0)) return '';
  if (s < 9.95) return `${s.toFixed(1)} s`;
  const r = Math.round(s);
  if (r < 60) return `${r} s`;
  return `${Math.floor(r / 60)} min ${r % 60} s`;
}
